import React from 'react';
import { Box, Button, FormControlLabel, Paper, Switch, TextField, Tooltip } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import Plot from 'react-plotly.js';
import * as Types from 'types/types';
import { Metadata, Node, PlotData, SendQueryDeps, config, layout, sendQuery } from './vectorData';
import { renderNodeFullContent, renderNodeMetaPanel } from './VectorNodePanels';

interface VectorVisualizerBodyProps {
  boxRef: React.RefObject<HTMLElement | null>;
  sx: unknown;
  view2D: boolean;
  setView2D: (value: boolean) => void;
  setResult: (value: Types.ChromaDBGetResponse | null) => void;
  plotlyRef: React.RefObject<unknown>;
  onNodeSelected: (metadata: Metadata) => void;
  plotData: PlotData[] | null;
  plotDimensions: { width: number; height: number };
  isMobile: boolean;
  node: Node | null;
  isControlled: boolean;
  inline?: boolean;
  querySet: Types.ChromaDBGetResponse;
  inputValue: string;
  queryDeps: SendQueryDeps;
  handleInputChange: (value: string) => void;
}

function VectorVisualizerBody(props: VectorVisualizerBodyProps): React.ReactElement {
  return (
    <Box className="VectorVisualizer" ref={props.boxRef} sx={{ ...(props.sx as object) }}>
      <Box sx={{ p: 0, m: 0, gap: 0 }}>
        <Paper
          sx={{
            p: 0.5,
            m: 0,
            display: 'flex',
            flexGrow: 0,
            height: 'auto',
            minHeight: 'auto',
            maxHeight: 'auto',
            position: 'relative',
            flexDirection: 'column',
          }}
        >
          <FormControlLabel
            sx={{
              display: 'flex',
              position: 'relative',
              width: 'fit-content',
              ml: 1,
              mb: '-2.5rem',
              zIndex: 100,
              flexBasis: 0,
              flexGrow: 0,
            }}
            control={<Switch checked={!props.view2D} />}
            onChange={(): void => {
              props.setView2D(!props.view2D);
              props.setResult(null);
            }}
            label="3D"
          />
          <Plot
            ref={props.plotlyRef}
            onClick={(event: { points: { customdata: Metadata }[] }): void =>
              props.onNodeSelected(event.points[0].customdata)
            }
            data={props.plotData}
            useResizeHandler={true}
            config={config}
            style={{
              display: 'flex',
              flexGrow: 1,
              minHeight: '240px',
              padding: 0,
              margin: 0,
              width: '100%',
              height: '100%',
              overflow: 'hidden',
            }}
            layout={{
              ...layout,
              width: props.plotDimensions.width,
              height: props.plotDimensions.height,
            }}
          />
        </Paper>
        <Paper
          sx={{
            display: 'flex',
            flexDirection: props.isMobile ? 'column' : 'row',
            mt: 0.5,
            p: 0.5,
            flexGrow: 1,
            minHeight: 'fit-content',
          }}
        >
          {props.node !== null && renderNodeMetaPanel(props.node, props.isMobile)}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 2,
              flexBasis: 0,
              flexShrink: 1,
            }}
          >
            {props.node === null && (
              <Paper sx={{ m: 0.5, p: 2, flexGrow: 1 }}>
                Click a point in the scatter-graph to see information about that node.
              </Paper>
            )}
            {props.node !== null && renderNodeFullContent(props.node)}
          </Box>
        </Paper>
        {!props.isControlled && !props.inline && !!props.querySet.query && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              flexGrow: 0,
              minHeight: '2.5rem',
              maxHeight: '2.5rem',
              height: '2.5rem',
              alignItems: 'center',
              mt: 1,
              pb: 0,
            }}
          >
            Query: {props.querySet.query}
          </Box>
        )}
        {!props.inline && (
          <Box className="Query" sx={{ display: 'flex', flexDirection: 'row', p: 1 }}>
            <TextField
              variant="outlined"
              fullWidth
              type="text"
              value={props.inputValue}
              onChange={(e): void => props.handleInputChange(e.target.value)}
              onKeyDown={(event): void => {
                if (event.key === 'Enter') sendQuery(props.inputValue, props.queryDeps);
              }}
              placeholder="Enter query to find related documents..."
              id="QueryInput"
            />
            <Tooltip title="Send">
              <Button
                sx={{ m: 1 }}
                variant="contained"
                onClick={(): void => {
                  sendQuery(props.inputValue, props.queryDeps);
                }}
              >
                <SendIcon />
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export { VectorVisualizerBody };
